import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, fonts, useMediaQuery } from "../../../Styles";
import { NewModal, MainContentPaddingTypes, NewInput, NewInputList, NewSelect, SystemControlsInner, ActivePensList, LF_PENLIST, NewButton } from "../..";
import { dispatchErrorWithTimeout, useLfContext } from "../_LocationFeeder/lfPenContext";
import { useSectionOptions } from "../../_Components";
import { ListStyle_Types, MenuFloats, NewFieldStyles, NewInputListHandles } from "../../../Definitions";
import { useOldSection } from "../../../Services";
import { useAppSelector } from "../../../Store";
import { selectSectionOptionBySystem } from "../../../Store/OptionsHandlers/optionsCombinedReducer";
import { isEqual, isNumber } from "lodash";

interface CalibrateLfSection_Props {
  isModalOpen: boolean;
  onClose: () => void;
  focus_view?: boolean;
}

export default function CalibrateLfSectionModal({ isModalOpen, onClose, focus_view }: CalibrateLfSection_Props) {
  const { t } = useTranslation()
  const { state: { systemId, selectedSection, selectedPens, domain, connection, error }, dispatch } = useLfContext();

  const [resolvedWeight, setResolvedWeight] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);

  const mobile = useMediaQuery(`(max-width: 700px)`) || focus_view === true;
  const abortControllerRef = useRef<AbortController | null>(null);
  const inputRef = useRef<NewInputListHandles | null>(null)

  // #region | Data fetch
  const { call: getLfSectionsInfo, data: lfSectionsState, updateConfigFromCalibrationLocation, doLocationCalibration } = useOldSection(connection);
  const sections = useMemo(() => (lfSectionsState[domain] ? lfSectionsState[domain].data.filter(section => section.system_id === systemId) : undefined), [domain, lfSectionsState, systemId]);

  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Options
  useSectionOptions();
  const sectionOptions = useAppSelector((state) => selectSectionOptionBySystem(state, [systemId]), isEqual);
  // #endregion

  // Set initial section, pens and selected pens
  useEffect(() => {
    if (!sections || sections.length === 0) return;

    const first = sections[0];

    if (!first?.section_locations) return;

    if (!selectedSection) {
      dispatch({
        selectedSection: first,
        // pens: first.section_locations,
        selectedPens: first.section_locations,
      });
    }
  }, [dispatch, sections, selectedSection]);

  // Abort prev operation
  function abortPreviousOperation() {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }

  // Reset result
  function resetResultState() {
    abortPreviousOperation();
    setLoading(false)
  }

  // #region | Do Calibration
  const doCalibration = async () => {
    const ref = inputRef.current;
    if (ref) {
      abortPreviousOperation();

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoading(true);

      const config = {
        triggerAmount: Number(ref.refs.current[0].value),
        pauseTime: Number(ref.refs.current[1].value),
        calibrationAmount: Number(ref.refs.current[2].value)
      }

      const results = await doLocationCalibration(selectedPens?.map(pen => pen.location_id) ?? [], config.triggerAmount, config.pauseTime, config.calibrationAmount, undefined, abortController.signal);
      if (!abortControllerRef.current) return;

      const newLocationResults: { [key: number]: boolean } = {};
      let errorOccured: boolean = false;

      for (let i = 0; i < results.length; i++) {
        let result = results[i];
        newLocationResults[result.id] = result.success;
        if (!result.success) {
          if (!errorOccured) {
            errorOccured = true;
            dispatchErrorWithTimeout(dispatch, { Elementid: 1, errorText: t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }) });
          }
        }
      } if (!errorOccured) {
        dispatchErrorWithTimeout(dispatch, { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) });
      }

      setLoading(false);
      dispatch({ locationResults: newLocationResults })
      abortControllerRef.current = null;
    }
  };
  // #endregion

  // #region | Update Resoolved Weight
  const UpdateResolvedWeight = async () => {
    const ref = inputRef.current;
    if (ref) {
      abortPreviousOperation();

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      setLoading(true);

      const calibrationAmount = Number(ref.refs.current[2].value);
      const resWeight = Math.round(resolvedWeight / calibrationAmount);

      if (!isNumber(resWeight) || isNaN(resWeight) || resWeight <= 0) {
        dispatchErrorWithTimeout(dispatch, { Elementid: 1, errorText: t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }) });
        setLoading(false);
        return;
      }

      const selectedPensIds = selectedPens?.map(e => e.location_id) ?? [];
      const results = await updateConfigFromCalibrationLocation(selectedPensIds.map(e => ({ id: e, gramPerRotation: resWeight })), undefined, abortController.signal);
      if (!abortControllerRef.current) return;

      const newLocationResults: { [key: number]: boolean } = {};

      let errorOccured: boolean = false;

      for (let i = 0; i < results.length; i++) {
        let result = results[i];
        newLocationResults[result.id] = result.success;
        if (!result.success) {
          if (!errorOccured) {
            errorOccured = true;
            dispatchErrorWithTimeout(dispatch, { Elementid: 1, errorText: t("systemControls.general.calibratingFailed", { defaultValue: "Calibrating failed" }) });
          }
        }
      }
      if (!errorOccured) {
        dispatchErrorWithTimeout(dispatch, { Elementid: 2, errorText: t("success", { defaultValue: "Success" }) });
      }

      setLoading(false);
      dispatch({ locationResults: newLocationResults })
      abortControllerRef.current = null;
    }
  }
  // #endregion

  if (!sections || !selectedSection) return null

  const currentIndex = sections.findIndex(s => s.section_id === selectedSection?.section_id);
  const allSelected = selectedSection.section_locations.every(id => selectedPens?.includes(id));

  return (selectedSection && (
    <NewModal
      header={t("systemControls.general.systemControls", { defaultValue: "System Controls" })}
      modalState={isModalOpen}
      responsiveOverrides={{
        focus_view: focus_view,
        mobile: mobile
      }}
      predefinedButtons={{
        onClose: () => { dispatch({ selectedSection: sections[0] }); resetResultState(); onClose() },
        onNext: () => { dispatch({ selectedSection: sections[(currentIndex + 1) % sections.length] }); resetResultState() },
        onPrev: () => { dispatch({ selectedSection: sections[(currentIndex - 1 + sections.length) % sections.length] }); resetResultState() },
      }}
      subHeader={selectedSection?.name
      }
      overrides={{
        backgroundColor: colors.greyLightTertiary,
        mainContentPadding: MainContentPaddingTypes.primary,
        mainContentScroll: mobile ? true : false,
        headerContentHeight: focus_view ? "60px" : undefined
      }}
      headerContent={
        <NewSelect
          options={sectionOptions ?? []}
          key={selectedSection.section_id}
          onChange={(i) => { resetResultState(); dispatch({ selectedSection: sections.find(section => section.section_id === i?.id) }) }}
          valueOverrides={{
            defaultValue: sectionOptions?.find(option => option.id === selectedSection.section_id)
          }}
          fieldOverrides={{
            fieldWidth: mobile ? "120px" : "160px",
            focus_view: focus_view,
            menuWidth: focus_view ? "70vw" : undefined,
            menuFloat: MenuFloats.center
          }}
        />
      }
      mainContent={
        <SystemControlsInner
          activeLocationsListHeader={(t("systemControls.general.calibrating", { defaultValue: "Calibrating" }))}
          focus_view={focus_view}
          locationVisual={
            <LF_PENLIST
              key={selectedSection?.section_id}
              focus_view={focus_view}
              section={selectedSection}
              pens={selectedSection?.section_locations}
            />
          }
          activeLocationsList={
            <ActivePensList
              focus_view={focus_view}
            />
          }
          error={{
            errorText: error.errorText,
            elementId: error.Elementid
          }}
          selectLocation={{
            selectedLocations: selectedPens ?? [],
            isAllSelected: allSelected,
            handleSelectAll: () => {
              if (allSelected) {
                dispatch({ selectedPens: selectedPens?.filter(penId => !selectedSection.section_locations.includes(penId)) });
              } else {
                const newSelection = Array.from(new Set([...selectedPens ?? [], ...selectedSection.section_locations]));
                dispatch({ selectedPens: newSelection });
              }
            }
          }}
          selectContent={
            <div>
              <NewInputList
                ref={inputRef}
                headers={["Trigger Amount", "Pause Time", "Calibration Amount"]}
                placeholders={["", "", ""]}
                isDisabled={[true, true, true]}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.greyTertiary,
                  focus_view: focus_view
                }}
                listOverrides={{
                  listStyle: ListStyle_Types.vertical,
                  listGap: focus_view ? "10px" : undefined
                }}
                valueOverrides={{
                  defaultValues: [10, 2, 5]
                }}
              />

              <div style={{ marginTop: "5px", display: "flex", flexDirection: "row", alignItems: "end", justifyContent: "space-between", zIndex: "3", width: "280px" }}>
                <NewInput
                  placeholder="g"
                  fieldOverrides={{
                    fieldStyle: NewFieldStyles.greyTertiary,
                    fieldWidth: "137px",
                    focus_view: focus_view
                  }}
                  header={`${t("systemControls.general.resolvedWeight", { defaultValue: "Resolved Weight" })} (${t("units.gram", { defaultValue: "g" })})`}
                  onChange={(e) => setResolvedWeight(Number(e))}
                  valueOverrides={{
                    valueType: "number",
                    minValueNumber: 0
                  }}
                />
                <NewButton
                  focus_view={focus_view}
                  content={(t("buttons.commit", { defaultValue: "commit" }))}
                  onClick={() => { resetResultState(); UpdateResolvedWeight() }}
                  overrides={{
                    baseStyles: { height: focus_view ? "40px" : "30px", width: "137px", backgroundColor: colors.greySecondary },
                    hoverStyles: { backgroundColor: colors.greyDarkSecondary }
                  }}
                  disabled={loading}
                />
              </div>
            </div>
          }
          button={{
            content: t("systemControls.general.calibrateSelected", { defaultValue: "Calibrate Selected" }),
            startEnhancer: "Calibrate",
            onClick: () => { resetResultState(); doCalibration() },
            disabled: loading
          }}
        />
      }
    />
  ))
}
